import { Injectable } from "@angular/core";
import { fromEvent, Observable } from "rxjs";
import { filter, map, share } from "rxjs/operators";

import { PlatformService } from "../platform/platform.service";
import { WindowRef } from "../platform/window";

import { BaseMessageEventService } from "./base-message-event.service";

@Injectable({
	providedIn: "root",
})
export class MessageEventService implements BaseMessageEventService {
	private observedWindowMap = new Map<Window, Observable<any>>();

	constructor(
		private windowRef: WindowRef,
		private platformService: PlatformService
	) {}

	/**
	 * returns a message observable mapped to the specified or default window and optionally
	 * filtered by an message event source
	 *
	 * @param destinationWindow window to observe for incoming messages (default if unspecified)
	 * @param origin allows only messages from the specified origin (any origin if unspecified)
	 */
	getMessage$<T>(destinationWindow?: Window, origin?: string): Observable<T> {
		const observedWindow = destinationWindow || this.windowRef.native;

		return this.getMessageEvent$(observedWindow).pipe(
			filter((messageEvent) => !origin || messageEvent.origin === origin),
			map((messageEvent) => messageEvent.data as T),
			share()
		);
	}

	/**
	 * posts the given message to the specified or default window
	 * @param message message object to post
	 * @param targetWindow target window (default if undefined)
	 * @param targetOrigin restricts to window of specified origin (or all "*" by default)
	 * @param transfer optional array of Transferable objects
	 */
	postMessage(message: any, targetWindow?: Window, targetOrigin = "*", transfer?: Transferable[]) {
		targetWindow = targetWindow || this.windowRef.native;
		targetWindow.postMessage(message, targetOrigin, transfer);
	}

	private getMessageEvent$(observedWindow: Window): Observable<MessageEvent> {
		let message$ = this.observedWindowMap.get(observedWindow);

		if (!message$) {
			message$ = this.platformService.forClient(() => fromEvent<MessageEvent>(observedWindow, "message"));
			this.observedWindowMap.set(observedWindow, message$);
		}

		return message$;
	}
}
